import { useState } from "react";
import { toast } from "sonner";
import { Save } from "lucide-react";
import { useSite, isValidSiteSha, getSiteSaveReadiness } from "../../lib/site";
import { adminUpdateSite } from "../../lib/api";

// Lists are edited as plain text: comma separated for chips, one per line for goals.
export function SiteTab() {
  const { site, loading, loadError, reload } = useSite();
  const [form, setForm] = useState(null);
  const [baseSha, setBaseSha] = useState(null);
  const [conflictSite, setConflictSite] = useState(null);
  const [saving, setSaving] = useState(false);

  if (form === null && loading) {
    return (
      <div
        className="p-4 rounded bg-panel border border-white/10 font-mono text-xs text-dim max-w-2xl"
        data-testid="admin-site-tab-loading"
      >
        $ loading site settings ...
      </div>
    );
  }

  // Initialise the form once the initial server request settles
  if (form === null && site) {
    setForm({
      bio: site.bio,
      toolset: site.toolset,
      goal_text: site.goal_text,
      github_url: site.github_url,
      studying: (site.studying || []).join(", "),
      horizon: (site.horizon || []).join(", "),
      goals: (site.goals || []).join("\n"),
    });
    setBaseSha(isValidSiteSha(site.content_sha) ? site.content_sha : null);
    return null;
  }
  if (!form) return null;

  const effectiveSha = conflictSite ? conflictSite.content_sha : (baseSha ?? site?.content_sha);
  const saveReadiness = getSiteSaveReadiness({
    site: conflictSite || site,
    loading,
    loadError,
    expectedSha: effectiveSha,
  });
  const canSave = saveReadiness.ready && !saving;

  const retryLoad = async () => {
    const reloaded = await reload();
    if (reloaded) {
      setForm({
        bio: reloaded.bio,
        toolset: reloaded.toolset,
        goal_text: reloaded.goal_text,
        github_url: reloaded.github_url,
        studying: (reloaded.studying || []).join(", "),
        horizon: (reloaded.horizon || []).join(", "),
        goals: (reloaded.goals || []).join("\n"),
      });
      setBaseSha(isValidSiteSha(reloaded.content_sha) ? reloaded.content_sha : null);
      setConflictSite(null);
    }
  };

  const save = async (e) => {
    e.preventDefault();
    if (!saveReadiness.ready) {
      toast.error(saveReadiness.message);
      return;
    }
    if (!form.bio.trim()) { toast.error("Bio cannot be empty"); return; }
    setSaving(true);
    const latestTexts = (conflictSite || site)?.texts || {};
    try {
      const updated = await adminUpdateSite({
        bio: form.bio.trim(),
        toolset: form.toolset.trim(),
        goal_text: form.goal_text.trim(),
        github_url: form.github_url.trim(),
        studying: form.studying.split(",").map((t) => t.trim()).filter(Boolean).slice(0, 12),
        horizon: form.horizon.split(",").map((t) => t.trim()).filter(Boolean).slice(0, 12),
        goals: form.goals.split("\n").map((t) => t.trim()).filter(Boolean).slice(0, 10),
        texts: latestTexts, // preserve inline-edited copy on the public pages
        content_sha: effectiveSha,
      });
      const reloaded = await reload();
      const nextSha = reloaded?.content_sha ?? updated?.content_sha;
      setBaseSha(isValidSiteSha(nextSha) ? nextSha : null);
      setConflictSite(null);
      toast.success("Site text updated — live on the public pages");
    } catch (err) {
      if (err?.response?.status === 409) {
        const latest = await reload();
        if (latest && isValidSiteSha(latest.content_sha)) {
          setConflictSite(latest);
          setBaseSha(latest.content_sha);
        }
        toast.error("Conflict: site settings changed in another tab. Review both versions before saving.");
      } else {
        toast.error("Save failed");
      }
    } finally {
      setSaving(false);
    }
  };

  const adoptServerVersion = () => {
    if (!conflictSite) return;
    setForm({
      bio: conflictSite.bio,
      toolset: conflictSite.toolset,
      goal_text: conflictSite.goal_text,
      github_url: conflictSite.github_url,
      studying: (conflictSite.studying || []).join(", "),
      horizon: (conflictSite.horizon || []).join(", "),
      goals: (conflictSite.goals || []).join("\n"),
    });
    setBaseSha(isValidSiteSha(conflictSite.content_sha) ? conflictSite.content_sha : null);
    setConflictSite(null);
  };

  const inputCls = "w-full px-3 py-2 rounded bg-deep border border-white/10 text-white text-xs font-mono focus:outline-none focus:border-cyber";

  return (
    <form onSubmit={save} className="space-y-4 max-w-2xl" data-testid="admin-site-tab">
      <p className="font-mono text-xs text-dim">
        This text appears on the public Home and About pages. Keep it honest — it is what reviewers read.
      </p>
      {!loading && !saveReadiness.ready && (
        <div
          className="p-4 rounded bg-red-500/10 border border-red-500/40 font-mono text-xs text-red-400 space-y-2"
          data-testid="admin-site-tab-load-error"
        >
          <p>{saveReadiness.message}</p>
          <button
            type="button"
            onClick={retryLoad}
            className="px-3 py-1 rounded border border-red-400/50 text-red-300 text-[11px] hover:bg-red-500/20 transition-colors"
            data-testid="site-tab-retry-load"
          >
            Retry Load
          </button>
        </div>
      )}
      {conflictSite && (
        <div className="p-4 rounded bg-amber-500/10 border border-amber-500/40 font-mono text-xs space-y-2" data-testid="site-tab-conflict">
          <p className="text-amber-300 font-bold">
            Conflict detected: Another tab saved new site settings. Your unsaved form edits are preserved below, and inline public text overrides have been synced.
          </p>
          <p className="text-dim text-[11px]">
            Server bio: {conflictSite.bio}
          </p>
          <button
            type="button"
            onClick={adoptServerVersion}
            className="px-3 py-1 rounded border border-amber-400/50 text-amber-300 text-[11px] hover:bg-amber-500/20 transition-colors"
            data-testid="site-tab-use-server"
          >
            Load Server Version into Form
          </button>
        </div>
      )}
      <div>
        <label className="block font-mono text-xs text-dim mb-1" htmlFor="sf-github">GitHub URL</label>
        <input id="sf-github" className={inputCls} value={form.github_url} maxLength={300}
          onChange={(e) => setForm({ ...form, github_url: e.target.value })} data-testid="site-form-github" />
        <p className="font-mono text-[10px] text-dim mt-1">Used by the navbar chip, footer, and every GitHub link on the site.</p>
      </div>
      <div>
        <label className="block font-mono text-xs text-dim mb-1" htmlFor="sf-bio">Bio (Home preview + About intro)</label>
        <textarea id="sf-bio" rows={4} className={inputCls} value={form.bio} maxLength={1000}
          onChange={(e) => setForm({ ...form, bio: e.target.value })} data-testid="site-form-bio" />
      </div>
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
        <div>
          <label className="block font-mono text-xs text-dim mb-1" htmlFor="sf-toolset">Primary toolset</label>
          <input id="sf-toolset" className={inputCls} value={form.toolset} maxLength={200}
            onChange={(e) => setForm({ ...form, toolset: e.target.value })} data-testid="site-form-toolset" />
        </div>
        <div>
          <label className="block font-mono text-xs text-dim mb-1" htmlFor="sf-goal">Goal (short line)</label>
          <input id="sf-goal" className={inputCls} value={form.goal_text} maxLength={200}
            onChange={(e) => setForm({ ...form, goal_text: e.target.value })} data-testid="site-form-goal" />
        </div>
      </div>
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
        <div>
          <label className="block font-mono text-xs text-dim mb-1" htmlFor="sf-studying">Currently studying (comma separated)</label>
          <input id="sf-studying" className={inputCls} value={form.studying}
            onChange={(e) => setForm({ ...form, studying: e.target.value })} data-testid="site-form-studying" />
        </div>
        <div>
          <label className="block font-mono text-xs text-dim mb-1" htmlFor="sf-horizon">On the horizon (comma separated)</label>
          <input id="sf-horizon" className={inputCls} value={form.horizon}
            onChange={(e) => setForm({ ...form, horizon: e.target.value })} data-testid="site-form-horizon" />
        </div>
      </div>
      <div>
        <label className="block font-mono text-xs text-dim mb-1" htmlFor="sf-goals">Learning goals (one per line)</label>
        <textarea id="sf-goals" rows={5} className={inputCls} value={form.goals}
          onChange={(e) => setForm({ ...form, goals: e.target.value })} data-testid="site-form-goals" />
      </div>
      <button type="submit" disabled={!canSave}
        className="flex items-center gap-2 px-5 py-2 rounded bg-cyber text-[#0b0f19] font-bold font-mono text-xs hover:bg-white transition-colors disabled:opacity-50"
        data-testid="site-form-save">
        <Save className="w-3.5 h-3.5" />{" "}
        {saving
          ? "Saving..."
          : loading
            ? "Loading Site Version..."
            : !saveReadiness.ready
              ? "Save Unavailable"
              : "Save Site Text"}
      </button>
    </form>
  );
}
